import { AlertTriangle, Zap } from 'lucide-react';
import { analyzeSemesterWorkload } from '../../utils/courseUtils';
import { useInstitutionConfig } from '../../contexts/InstitutionConfigContext';

interface WorkloadCourse {
  credits?: number;
  grade_data?: {
    difficulty?: string | null;
    average_gpa?: number | null;
  } | null;
}

export interface WorkloadIndicatorProps {
  courses: WorkloadCourse[] | null | undefined;
}

/**
 * Shows moderate (yellow) or heavy (red) workload warnings based on credit
 * load, number of hard/challenging courses, and average historical GPA.
 */
const WorkloadIndicator = ({ courses }: WorkloadIndicatorProps) => {
  // Measured against the active institution's own load scale (30 ECTS is a
  // normal semester at UNIC, a very heavy one on a US 18-credit scale).
  const { config } = useInstitutionConfig();

  if (!courses || !courses.length) {
    return null;
  }

  const analysis = analyzeSemesterWorkload(courses, config.workloadThresholds);

  if (analysis.level === 'light') {
    return null;
  }

  const colorClasses: Record<string, string> = {
    moderate: 'bg-status-warning/10 border-status-warning/25 text-status-warning-fg',
    heavy: 'bg-status-critical/10 border-status-critical/25 text-status-critical-fg',
  };

  const iconColorClasses: Record<string, string> = {
    moderate: 'text-status-warning',
    heavy: 'text-status-critical',
  };

  const Icon = analysis.level === 'heavy' ? AlertTriangle : Zap;

  const { difficulties, averageGpa } = analysis.details;
  const hasGradeData = averageGpa !== null;
  const hardCount = difficulties.hard + difficulties.challenging;

  return (
    <div
      className={`mt-2 p-2 rounded-md border animate-fade-in print-hide ${colorClasses[analysis.level]}`}
      role="alert"
    >
      <div className="flex items-start gap-2">
        <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${iconColorClasses[analysis.level]}`} />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium">
            {analysis.level === 'heavy' ? 'Heavy workload' : 'Moderate workload'}
          </p>
          {analysis.message && (
            <p className="text-xs mt-0.5 opacity-90">{analysis.message}</p>
          )}
          {hasGradeData && hardCount > 0 && (
            <p className="text-xs mt-1 opacity-75">
              {hardCount} {hardCount === 1 ? 'course' : 'courses'} rated challenging/hard
              {averageGpa != null && ` · Avg GPA: ${averageGpa.toFixed(2)}`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkloadIndicator;
